// Estado que NÃO existe no servidor: qual card comum caiu em cada fase, se o
// aluno já viu a apresentação do mascote, qual avatar ele escolheu e se já viu
// o encerramento.
//
// E, desde 24/08/2026, um sorteio que não existe em lugar NENHUM: quais fases raras
// entregam carta. Ele é derivado a cada carga de (aluno, atividade) — ver
// `derivarSorteioRaras`, no fim deste arquivo, que explica por que este é derivado e
// o dos comuns é guardado.
//
// Nada disso é progresso — as fases concluídas vêm do `item_percentage` da API.
// É estado de experiência, e por isso mora no navegador (decisão I-7).
//
// Chave composta por (aluno, ATIVIDADE), não uma chave única: o mesmo aluno pode
// ter várias atividades em modo trilha, e uma sobrescreveria o sorteio e o
// avatar da outra em silêncio (decisão 26 do backlog do protótipo).
//
// Atividade e não alocação (mudou em 01/08/2026): a execução do aluno no AVA já é
// por atividade, então realocar a mesma trilha para a turma reencontra as mesmas
// fases concluídas. Chavear por alocação faria essa "trilha nova" nascer completa
// e com sorteio novo — cartas ganhas sem jogar, quando a coleção entrar no ar.

import {
  aleatorioComSemente,
  embaralharComSemente
} from "../nucleo/aleatorioComSemente";
import {
  CHANCE_DE_CARTA_RARA,
  sortearComuns,
  sortearRaras
} from "../nucleo/recompensaDaFase";

// A versão no prefixo é o botão de reset global: subir invalida tudo que está
// guardado no navegador de todo mundo, sem pedir a ninguém para limpar o
// localStorage. Subir quando o formato mudar.
//
// Para reexperimentar o onboarding sem mexer aqui, use `?trilha=reset` na URL
// (ver pediuReset, no fim do arquivo) — é o caminho preferido; esta constante é
// para mudança de formato ou reset de todo mundo de uma vez.
// v4 (28/07/2026): o formato mudou — `cardsColetados` saiu do objeto guardado,
// porque o álbum do encerramento passou a ser derivado das fases concluídas (ver
// nucleo/album.js). Subir a versão é o que faz todo navegador que já tinha uma
// trilha em andamento voltar à primeira entrada, sem pedir a ninguém para limpar
// o localStorage à mão — o que também era necessário para reexperimentar a
// abertura e a escolha do explorador depois do reset do servidor.
// v5 (31/07/2026): o sorteio dos cards comuns deixou de ser aleatório e passou a
// ser DERIVADO. Subir a versão aqui é o que faz quem já tem um sorteio aleatório
// guardado passar para o derivado — sem isso, esses alunos continuariam com cards
// diferentes em cada dispositivo, que é justamente o defeito que a mudança
// conserta.
// v6 (01/08/2026): a chave e a semente passaram de (aluno, ALOCAÇÃO) para
// (aluno, ATIVIDADE). O motivo é do backend: a execução do aluno no AVA já é por
// atividade, então realocar a mesma trilha para a turma reencontra as mesmas
// fases concluídas. Com a chave por alocação, essa "trilha nova" nasceria
// completa E com um sorteio novo — o aluno ganharia cartas sem jogar quando a
// coleção entrar no ar. Subir a versão faz todo mundo rederivar sob a chave nova.
const PREFIXO = "tl:v6";

export function chaveProgresso(userId, activityId) {
  return `${PREFIXO}:${userId}:${activityId}`;
}

// Não há `cardsColetados` aqui de propósito. Houve, e foi removido: o álbum do
// encerramento é derivado das fases concluídas cruzadas com `sorteioComuns` (ver
// nucleo/album.js). Uma lista escrita ao fechar a cerimônia perdia o card quando
// o aluno recarregava a página com o modal aberto, e era uma segunda fonte de
// verdade sobre o que ele já fez — que o tabuleiro tira do servidor.
function vazio() {
  return {
    sorteioComuns: {},
    introVisto: false,
    encerramentoVisto: false,
    avatarId: null
  };
}

// localStorage lança em modo privado de alguns navegadores e quando a cota
// estoura. Nenhuma dessas coisas pode impedir o aluno de fazer a atividade —
// no pior caso ele perde o sorteio guardado e vê outros cards.
function lerCru(chave) {
  try {
    const bruto = window.localStorage.getItem(chave);
    if (!bruto) return null;
    const dados = JSON.parse(bruto);
    return dados && typeof dados === "object" ? dados : null;
  } catch (erro) {
    return null;
  }
}

function escrever(chave, dados) {
  try {
    window.localStorage.setItem(chave, JSON.stringify(dados));
  } catch (erro) {
    // segue sem persistir
  }
}

// O sufixo da semente da POSIÇÃO das cartas comuns. Terceiro sufixo do módulo, e
// pela mesma razão do das raras: a POSIÇÃO e a ORDEM das cartas são dois sorteios
// diferentes sobre a mesma trilha, e com a mesma semente ficariam correlacionados —
// mexer no jitter moveria também QUAL carta cai em cada fase. Sufixos separados
// deixam os dois botões independentes.
const SUFIXO_POSICOES = "posicoes";

/**
 * Lê o progresso local, derivando o sorteio na primeira vez.
 *
 * O sorteio é DERIVADO de (aluno, atividade), não aleatório — ver
 * nucleo/aleatorioComSemente.js. Consequência que vale saber: ele continua sendo
 * gravado, mas o valor gravado deixou de ser a única fonte. Um aluno que abre a
 * trilha em outro dispositivo, ou que teve o `localStorage` limpo, recebe
 * exatamente os mesmos cards em vez de um sorteio novo.
 *
 * Continua sendo gravado porque o derivado depende do BANCO de cards da região:
 * se um card for acrescentado ou removido de `conteudo/`, a derivação muda para
 * todo mundo, inclusive para quem está no meio da trilha. O que está guardado
 * congela o que o aluno já viu; a derivação é o valor inicial e a rede para quem
 * não tem nada guardado.
 *
 * @param {Function} [embaralhar] injetável pelo teste. Em runtime ninguém passa:
 *   o padrão é o embaralhamento derivado de (userId, activityId).
 */
export function carregarProgressoLocal(
  userId,
  activityId,
  totalFases,
  cardsComuns,
  embaralhar = embaralharComSemente(userId, activityId),
  aleatorio = aleatorioComSemente(userId, activityId, SUFIXO_POSICOES)
) {
  // Sem fases não há o que sortear, e persistir um sorteio vazio agora faria a
  // trilha nunca mais sortear (o vazio seria lido como "já sorteado").
  if (!totalFases || totalFases <= 0) return vazio();

  const chave = chaveProgresso(userId, activityId);
  const salvo = lerCru(chave);

  if (
    salvo &&
    salvo.sorteioComuns &&
    Object.keys(salvo.sorteioComuns).length > 0
  ) {
    return { ...vazio(), ...salvo };
  }

  // Trilha nova (ou salvo corrompido): sorteia e grava.
  //
  // O MAPA VAZIO É UM RESULTADO LEGÍTIMO desde 24/08/2026: uma trilha de até 4
  // fases não entrega comum nenhuma (ver `QUANTAS_COMUNS`). Ele é gravado e lido
  // como "ainda não sorteou" — o que não faz diferença, porque rederivar dá o mesmo
  // vazio de novo. Não vale a pena um segundo campo para distinguir os dois casos.
  const novo = {
    ...vazio(),
    ...(salvo || {}),
    sorteioComuns: sortearComuns(totalFases, cardsComuns, aleatorio, embaralhar)
  };
  escrever(chave, novo);
  return novo;
}

/**
 * O sorteio dos comuns DERIVADO, sem ler nem gravar nada.
 *
 * Existe só para o override de QA `?cartas=<n>` (ver ./visualizacao.js). O caminho
 * normal é `carregarProgressoLocal`, que grava — e é justamente por gravar que o
 * override precisa desta porta: com `?cartas=3` a primeira carga escreveria o layout
 * forçado no `localStorage` do aluno e a segunda o leria de lá, dando a impressão de
 * que o parâmetro parou de funcionar. Pior, `corpoDeSincronia` o mandaria ao
 * servidor, e aí o estado de QA sobreviveria à limpeza do navegador.
 *
 * Quem chama garante que nada disso aconteça: `TrilhaDeAtividade` usa este valor no
 * lugar do guardado e SUSPENDE a sincronia enquanto o override estiver ligado.
 *
 * @param {number} userId
 * @param {number} activityId
 * @param {number} totalFases
 * @param {Array}  cardsComuns
 * @param {number} [quantas]  o `k` forçado
 * @returns {Object} mapa { [numeroDaFase]: idDoCard }
 */
export function derivarSorteioComuns(
  userId,
  activityId,
  totalFases,
  cardsComuns,
  quantas = null
) {
  if (!totalFases || totalFases <= 0) return {};
  return sortearComuns(
    totalFases,
    cardsComuns,
    aleatorioComSemente(userId, activityId, SUFIXO_POSICOES),
    embaralharComSemente(userId, activityId),
    quantas
  );
}

// O sufixo da semente das RARAS. Diferente do sorteio dos comuns de propósito: com
// a mesma semente os dois resultados ficariam correlacionados — a fase que recebeu o
// primeiro card do baralho embaralhado seria sempre a mesma que tirou a primeira
// moeda —, e correlação num sorteio é o tipo de defeito que ninguém vê e ninguém
// prova.
const SUFIXO_RARAS = "raras";

/**
 * Em quais fases raras a CARTA cai, para este aluno nesta trilha.
 *
 * DERIVADO A CADA CARGA, e não guardado — é a diferença deste sorteio para o dos
 * comuns, e vale explicar porque parece incoerência:
 *
 * - o dos COMUNS é guardado porque depende do BANCO de cards da região. Acrescentar
 *   ou remover um card muda a derivação para todo mundo, inclusive para quem está no
 *   meio da trilha, e o que está guardado congela o que o aluno já viu;
 * - o das RARAS depende só de (aluno, atividade) e da constante da chance. Não há
 *   banco para mudar debaixo dele, então derivar já dá o mesmo resultado em qualquer
 *   dispositivo, sem chave nova no `localStorage`, sem coluna nova no servidor e sem
 *   migração.
 *
 * O TRADE-OFF, declarado: se `CHANCE_DE_CARTA_RARA` mudar, alunos com trilha em
 * andamento mudam de resultado — quem tinha a rara pode deixar de ter. Se isso
 * incomodar, a mitigação é guardar o mapa junto do `drawn_cards` que já vai ao
 * servidor e subir `tl:v6` para `tl:v7`. Não foi feito agora porque a chance não é
 * configurável: mexer nela é decisão de produto, e decisão de produto avisa.
 *
 * @param {number} userId
 * @param {number} activityId
 * @param {number} totalFases
 * @param {number} [chance]  só o override de QA passa outro valor (ver
 *   ./visualizacao.js) — em runtime é a decisão de produto
 * @returns {Object} mapa { [numeroDaFase]: true }
 */
export function derivarSorteioRaras(
  userId,
  activityId,
  totalFases,
  chance = CHANCE_DE_CARTA_RARA
) {
  if (!totalFases || totalFases <= 0) return {};
  return sortearRaras(
    totalFases,
    chance,
    aleatorioComSemente(userId, activityId, SUFIXO_RARAS)
  );
}

export function salvarProgressoLocal(userId, activityId, dados) {
  escrever(chaveProgresso(userId, activityId), dados);
  return dados;
}

/**
 * Apaga o progresso local desta trilha, para reexperimentar a abertura, a
 * escolha do explorador e o sorteio dos cards do zero.
 *
 * Existe porque testar o onboarding é, por definição, testar o que só acontece
 * uma vez — e a alternativa era subir a versão da chave a cada rodada de QA
 * (poluindo o histórico) ou pedir para alguém rodar comandos no console.
 */
export function limparProgressoLocal(userId, activityId) {
  try {
    window.localStorage.removeItem(chaveProgresso(userId, activityId));
  } catch (erro) {
    // localStorage indisponível: não havia nada guardado de qualquer forma.
  }
}

// Preferência de TELA CHEIA. Chave própria, sem aluno nem atividade no nome, e
// fora do `PREFIXO` versionado — as três coisas são de propósito:
//
// - é preferência de VISUALIZAÇÃO do dispositivo, não estado da trilha: quem
//   gosta de tela cheia gosta em toda atividade, e ter de religar a cada uma
//   seria o mesmo que não persistir;
// - não entra no objeto do progresso porque `limparProgressoLocal` e o
//   `?trilha=reset` (que existem para reexperimentar o onboarding) apagariam a
//   preferência de quebra, sem ninguém ter pedido isso;
// - fora do prefixo versionado porque não tem formato para migrar. É um
//   booleano; subir a versão da chave do progresso não deve religar o modo.
//
// Guarda "1"/"0" em vez de JSON: qualquer outro valor — chave ausente, lixo de
// uma versão futura, localStorage indisponível — cai em "desligado", que é o
// estado seguro (a trilha no fluxo da página nunca esconde nada do AVA).
const CHAVE_TELA_CHEIA = "tl:tela-cheia";

export function carregarPreferenciaTelaCheia() {
  try {
    return window.localStorage.getItem(CHAVE_TELA_CHEIA) === "1";
  } catch (erro) {
    return false;
  }
}

export function salvarPreferenciaTelaCheia(ativo) {
  try {
    window.localStorage.setItem(CHAVE_TELA_CHEIA, ativo ? "1" : "0");
  } catch (erro) {
    // segue sem persistir: o modo continua valendo nesta sessão
  }
  return ativo;
}

/**
 * Detecta o pedido de reset na URL: `?trilha=reset`.
 *
 * Vale por CARGA de página: recarregar com o parâmetro reseta de novo, e é isso
 * que se quer de um atalho de QA. Depois de escolher o explorador, a escolha
 * sobrevive à sessão normalmente (o refetch da atividade não remonta a trilha).
 */
export function pediuReset(location) {
  const busca = location && location.search;
  if (!busca) return false;
  return new URLSearchParams(busca).get("trilha") === "reset";
}
